import { useEffect, useRef, useState } from 'react';
import { matchesStreamChannel } from './channel.js';
import { classifyStreamHealth, IDLE_STREAM_HEALTH, mergeStreamHealth, } from './health.js';
import { closePeerConnection, createLivePeerConnection, toRtcIceCandidateInit } from './peer.js';
import { deriveLinkRates, readPeerLinkCounters } from './stats.js';
const MAX_VIEWER_PEERS = 8;
const HEALTH_POLL_MS = 2_000;
const REMOTE_HEALTH_TTL_MS = 15_000;
export function useLivePublisher({ enabled, showId, localPeerId, mediaStream, sendSignal, subscribe, channel = 'show', }) {
    const [health, setHealth] = useState(IDLE_STREAM_HEALTH);
    const peersRef = useRef(new Map());
    const streamRef = useRef(mediaStream);
    streamRef.current = mediaStream;
    const sendRef = useRef(sendSignal);
    sendRef.current = sendSignal;
    const localPeerIdRef = useRef(localPeerId);
    localPeerIdRef.current = localPeerId;
    const pendingIceRef = useRef(new Map());
    const remoteReportsRef = useRef(new Map());
    useEffect(() => {
        if (!enabled || !showId || !mediaStream || !localPeerId) {
            for (const peer of peersRef.current.values()) {
                closePeerConnection(peer);
            }
            peersRef.current.clear();
            pendingIceRef.current.clear();
            remoteReportsRef.current.clear();
            setHealth(IDLE_STREAM_HEALTH);
            return;
        }
        const currentShowId = showId;
        const startedAt = Date.now();
        const prevByPeer = new Map();
        const attachTracks = (peer) => {
            const stream = streamRef.current;
            if (!stream) {
                return;
            }
            const existing = new Set(peer.getSenders().map((sender) => sender.track));
            for (const track of stream.getTracks()) {
                if (!existing.has(track)) {
                    peer.addTrack(track, stream);
                }
            }
        };
        const offerTo = async (peerId) => {
            if (peerId === localPeerIdRef.current) {
                return;
            }
            if (peersRef.current.size >= MAX_VIEWER_PEERS && !peersRef.current.has(peerId)) {
                return;
            }
            let peer = peersRef.current.get(peerId);
            if (peer) {
                closePeerConnection(peer);
                peersRef.current.delete(peerId);
            }
            pendingIceRef.current.delete(peerId);
            peer = createLivePeerConnection();
            peersRef.current.set(peerId, peer);
            attachTracks(peer);
            peer.onicecandidate = (event) => {
                if (!event.candidate) {
                    return;
                }
                sendRef.current({
                    showId: currentShowId,
                    kind: 'ice',
                    toPeerId: peerId,
                    channel,
                    candidate: {
                        candidate: event.candidate.candidate,
                        sdpMid: event.candidate.sdpMid,
                        sdpMLineIndex: event.candidate.sdpMLineIndex,
                        usernameFragment: event.candidate.usernameFragment,
                    },
                });
            };
            peer.onconnectionstatechange = () => {
                if (peer.connectionState === 'failed' || peer.connectionState === 'closed') {
                    closePeerConnection(peer);
                    peersRef.current.delete(peerId);
                    remoteReportsRef.current.delete(peerId);
                }
            };
            const offer = await peer.createOffer();
            await peer.setLocalDescription(offer);
            const sdp = peer.localDescription?.sdp;
            if (!sdp) {
                return;
            }
            sendRef.current({
                showId: currentShowId,
                kind: 'offer',
                toPeerId: peerId,
                channel,
                sdp,
            });
        };
        const unsubscribe = subscribe((signal) => {
            if (signal.showId !== currentShowId || signal.fromPeerId === localPeerIdRef.current) {
                return;
            }
            if (!matchesStreamChannel(signal, channel)) {
                return;
            }
            if (signal.kind === 'viewer.hello') {
                void offerTo(signal.fromPeerId);
                return;
            }
            if (signal.kind === 'health' && signal.health) {
                remoteReportsRef.current.set(signal.fromPeerId, {
                    issues: signal.health.issues,
                    at: Date.now(),
                });
                return;
            }
            if (signal.kind === 'answer' && signal.sdp) {
                const peer = peersRef.current.get(signal.fromPeerId);
                if (!peer || peer.signalingState === 'stable') {
                    return;
                }
                void (async () => {
                    await peer.setRemoteDescription({ type: 'answer', sdp: signal.sdp ?? '' });
                    const queued = pendingIceRef.current.get(signal.fromPeerId) ?? [];
                    pendingIceRef.current.delete(signal.fromPeerId);
                    for (const candidate of queued) {
                        await peer.addIceCandidate(toRtcIceCandidateInit(candidate));
                    }
                })();
                return;
            }
            if (signal.kind === 'ice' && signal.candidate) {
                const peer = peersRef.current.get(signal.fromPeerId);
                if (!peer || peer.remoteDescription === null) {
                    const queued = pendingIceRef.current.get(signal.fromPeerId) ?? [];
                    queued.push(signal.candidate);
                    pendingIceRef.current.set(signal.fromPeerId, queued);
                    return;
                }
                void peer.addIceCandidate(toRtcIceCandidateInit(signal.candidate));
                return;
            }
            if (signal.kind === 'peer.left') {
                const peer = peersRef.current.get(signal.fromPeerId);
                closePeerConnection(peer);
                peersRef.current.delete(signal.fromPeerId);
                pendingIceRef.current.delete(signal.fromPeerId);
                remoteReportsRef.current.delete(signal.fromPeerId);
            }
        });
        const pollHealth = async () => {
            const now = Date.now();
            const stream = streamRef.current;
            const hasExpectedVideo = Boolean(stream?.getVideoTracks().some((track) => track.readyState === 'live' && track.enabled));
            const hasExpectedAudio = Boolean(stream?.getAudioTracks().some((track) => track.readyState === 'live' && track.enabled));
            const connected = [...peersRef.current.entries()].filter(([, peer]) => peer.connectionState === 'connected' || peer.connectionState === 'connecting');
            const connectionFailed = [...peersRef.current.values()].some((peer) => peer.connectionState === 'failed' || peer.connectionState === 'disconnected');
            let videoBitrateKbps = null;
            let audioBitrateKbps = null;
            let videoFps = null;
            let packetLossPct = null;
            let rttMs = null;
            for (const [peerId, peer] of connected) {
                const counters = await readPeerLinkCounters(peer, 'outbound');
                const prev = prevByPeer.get(peerId);
                const rates = deriveLinkRates(prev?.counters ?? null, counters, prev ? now - prev.at : 0);
                prevByPeer.set(peerId, { counters, at: now });
                if (rates.videoBitrateKbps !== null) {
                    videoBitrateKbps =
                        videoBitrateKbps === null
                            ? rates.videoBitrateKbps
                            : Math.min(videoBitrateKbps, rates.videoBitrateKbps);
                }
                if (rates.audioBitrateKbps !== null) {
                    audioBitrateKbps =
                        audioBitrateKbps === null
                            ? rates.audioBitrateKbps
                            : Math.min(audioBitrateKbps, rates.audioBitrateKbps);
                }
                if (rates.videoFps !== null) {
                    videoFps = videoFps === null ? rates.videoFps : Math.min(videoFps, rates.videoFps);
                }
                if (rates.packetLossPct !== null) {
                    packetLossPct =
                        packetLossPct === null ? rates.packetLossPct : Math.max(packetLossPct, rates.packetLossPct);
                }
                if (counters.rttMs !== null) {
                    rttMs = rttMs === null ? counters.rttMs : Math.max(rttMs, counters.rttMs);
                }
            }
            const classified = classifyStreamHealth({
                side: 'publisher',
                warmupDone: now - startedAt > 4_000,
                connectedPeers: connected.length,
                connectionFailed,
                hasExpectedVideo,
                hasExpectedAudio,
                videoBitrateKbps,
                audioBitrateKbps,
                videoFps,
                packetLossPct,
                rttMs,
            });
            for (const [peerId, report] of remoteReportsRef.current) {
                if (now - report.at > REMOTE_HEALTH_TTL_MS) {
                    remoteReportsRef.current.delete(peerId);
                }
            }
            const remoteIssues = [];
            let remoteIssueCount = 0;
            for (const report of remoteReportsRef.current.values()) {
                if (report.issues.length === 0) {
                    continue;
                }
                remoteIssueCount += 1;
                remoteIssues.push(...report.issues);
            }
            const local = {
                ...IDLE_STREAM_HEALTH,
                severity: classified.severity,
                issues: classified.issues,
                bitrateKbps: videoBitrateKbps,
                rttMs,
                packetLossPct,
                connectedPeers: connected.length,
            };
            setHealth(mergeStreamHealth(local, remoteIssues, remoteIssueCount));
        };
        sendRef.current({ showId: currentShowId, kind: 'publisher.ready', channel });
        void pollHealth();
        const pollId = window.setInterval(() => {
            void pollHealth();
        }, HEALTH_POLL_MS);
        return () => {
            window.clearInterval(pollId);
            unsubscribe();
            for (const peer of peersRef.current.values()) {
                closePeerConnection(peer);
            }
            peersRef.current.clear();
            pendingIceRef.current.clear();
            remoteReportsRef.current.clear();
            setHealth(IDLE_STREAM_HEALTH);
        };
    }, [enabled, showId, localPeerId, mediaStream, subscribe, channel]);
    return { health };
}
//# sourceMappingURL=useLivePublisher.js.map